import { Boss } from './boss-base';
import { AVATAR, AV, MODE_TINT } from './avatar-modes';
import { GAME } from '@/config/game';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

export { AVATAR } from './avatar-modes';

/**
 * B11 — The Avatar (finale+). The closer of the ELEMENTALS arc:
 * it cycles through five element MODES (one per HP phase), each channeling a
 * strengthened version of that Elemental's signature — Pyre's flame wall,
 * Tide's vortex, Null's event horizon, Terra's slam, Gale's gust — on top of
 * that element's bullet language. Data lives in `avatar-modes.ts`. docs/02.
 */
export class AvatarBoss extends Boss {
  private mode = 0;
  private sig = 0; // signature attack timer (reset per mode)
  private sigWind = 0; // windup sub-state (base owns `windup`)
  private active = 0; // vortex/gust active window
  private dir = 0.7;
  private shielded = false;
  private swallowed = 0;
  private tornadoTimer = 7;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(AVATAR, level, x, y, homeX, homeY);
    this.allowReinforce = false; // its own rotating signatures are the threat
  }

  override get isVulnerable(): boolean {
    // Only the void phase's event horizon blocks damage; all else is open
    return super.isVulnerable && !(this.mode === 2 && this.shielded);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;

    // Phase index drives the element mode; reset signature state on a switch
    if (this.phaseIndex !== this.mode && this.phaseIndex >= 0) {
      this.mode = this.phaseIndex;
      this.sig = 2.0;
      this.sigWind = this.active = 0;
      this.shielded = false;
      this.swallowed = 0;
    }

    switch (this.mode) {
      case 0: this.fire(dt, px, bullets); break;
      case 1: this.water(dt, bullets); break;
      case 2: this.voidMode(dt, bullets); break;
      case 3: this.earth(dt, bullets, telegraphs); break;
      case 4: this.air(dt, bullets); break;
    }
  }

  // FIRE — sweeping flame wall toward the player's side (Pyre signature)
  private fire(dt: number, px: number, bullets: BulletSystem): void {
    this.sig -= dt;
    if (this.sig > 0) return;
    this.sig = AV.wallInterval / this.intensity;
    const inset = GAME.arena.wallThickness + AV.wallInset;
    const fromLeft = px > this.arenaW / 2;
    const startX = fromLeft ? inset : this.arenaW - inset;
    const dirX = fromLeft ? 1 : -1;
    const speed = AV.wallSpeed * this.bulletSpeedMul;
    const life = (this.arenaW - inset * 2) / speed;
    for (let y = inset; y <= this.arenaH - inset; y += AV.wallGap) {
      bullets.spawn({
        x: startX, y, dirX, dirY: 0, speed, life, radius: 13, damage: 2,
        friendly: false, texKey: 'bullet/enemy/fire', rotates: false,
      });
    }
  }

  // WATER — telegraphed undertow that drags the ship in, then a snap ring
  private water(dt: number, bullets: BulletSystem): void {
    if (this.active > 0) {
      this.active -= dt;
      if (this.active <= 0) this.burst(bullets, 14, AV.burstSpeed.water, 'bullet/enemy/ball');
    } else if (this.sigWind > 0) {
      this.sigWind -= dt;
      if (this.sigWind <= 0) this.active = AV.vortexTime;
    } else {
      this.sig -= dt;
      if (this.sig <= 0) {
        this.sig = AV.vortexInterval / this.intensity;
        this.sigWind = AV.vortexWindup;
      }
    }
  }

  // VOID — event horizon: swallow bullets, spit them back, blink (Null)
  private voidMode(dt: number, bullets: BulletSystem): void {
    this.sig -= dt;
    if (this.sig > 0) return;
    if (this.shielded) {
      this.shielded = false;
      this.sig = AV.shieldOpen;
      this.burst(bullets, Math.min(this.swallowed, AV.spitCap), AV.burstSpeed.void, 'bullet/enemy/void');
      this.swallowed = 0;
    } else {
      this.shielded = true;
      this.sig = AV.shieldClosed / this.levelSpeedMul;
      this.dir += 2.39996; // golden-angle blink
      this.x = this.prevX = this.homeX + Math.cos(this.dir) * 300;
      this.y = this.prevY = this.homeY + Math.sin(this.dir * 1.3) * 170;
    }
  }

  // EARTH — seismic slam: crack telegraph → shockwave ring + boulder rain
  private earth(dt: number, bullets: BulletSystem, telegraphs?: TelegraphSink): void {
    if (this.sigWind > 0) {
      this.sigWind -= dt;
      if (this.sigWind <= 0) this.slam(bullets);
      return;
    }
    this.sig -= dt;
    if (this.sig <= 0) {
      this.sig = AV.slamInterval / this.intensity;
      this.sigWind = AV.slamWindup;
      if (telegraphs) {
        for (let i = 0; i < 7; i++) {
          const a = (Math.PI * 2 * i) / 7 + this.sig;
          telegraphs.crack(this.x, this.y, Math.cos(a), Math.sin(a), AV.crackLength, AV.slamWindup);
        }
      }
    }
  }

  private slam(bullets: BulletSystem): void {
    this.burst(bullets, 18, AV.burstSpeed.earth, 'bullet/enemy/rock', 2);
    // Boulder rain across the REAL arena width (X3)
    const inset = GAME.arena.wallThickness + AV.wallInset;
    const speed = AV.rainSpeed * this.bulletSpeedMul;
    const life = (this.arenaH - inset * 2) / speed;
    for (let i = 0; i < AV.rainRocks; i++) {
      bullets.spawn({
        x: inset + ((this.arenaW - inset * 2) * i) / (AV.rainRocks - 1), y: inset,
        dirX: 0, dirY: 1, speed, life, radius: 12, damage: 2,
        friendly: false, texKey: 'bullet/enemy/rock', rotates: false,
      });
    }
    this.events?.burst(this.x, this.y, BOSS_FEEDBACK.slam);
  }

  // AIR — gust shove (windPush via the fight step) + tornado weaver adds
  private air(dt: number, bullets: BulletSystem): void {
    void bullets;
    if (this.active > 0) {
      this.active -= dt;
    } else if (this.sigWind > 0) {
      this.sigWind -= dt;
      if (this.sigWind <= 0) this.active = AV.gustTime;
    } else {
      this.sig -= dt;
      if (this.sig <= 0) {
        this.sig = AV.gustInterval / this.intensity;
        this.sigWind = AV.gustWindup;
        this.dir += 2.39996;
      }
    }
    this.tornadoTimer -= dt;
    if (this.tornadoTimer <= 0) {
      this.tornadoTimer = 8;
      if ((this.minionCount?.() ?? 0) < 4) this.spawnMinions?.('weaver', 2);
    }
  }

  /** Even radial burst helper shared by the water/void/earth signatures. */
  private burst(
    bullets: BulletSystem, count: number, speed: number, tex: string, damage = 1,
  ): void {
    for (let i = 0; i < count; i++) {
      const a = (Math.PI * 2 * i) / Math.max(1, count);
      bullets.spawn({
        x: this.x, y: this.y, dirX: Math.cos(a), dirY: Math.sin(a),
        speed: speed * this.bulletSpeedMul, life: 4, radius: 10, damage,
        friendly: false, texKey: tex, rotates: false,
      });
    }
  }

  /** Water pulls the ship in, air shoves it out — applied by combat/fight-step.ts. */
  override environmentalForce(px: number, py: number): { x: number; y: number } | null {
    if (this.dead) return null;
    if (this.mode === 1 && this.active > 0) {
      const dx = this.x - px;
      const dy = this.y - py;
      const d = Math.hypot(dx, dy);
      if (d < this.def.radius + 20 || d > AV.vortexRange) return null;
      const s = AV.vortexPull * (1 - d / AV.vortexRange);
      return { x: (dx / d) * s, y: (dy / d) * s };
    }
    if (this.mode === 4 && this.active > 0) {
      return { x: Math.cos(this.dir) * AV.gustPush, y: Math.sin(this.dir) * AV.gustPush };
    }
    return null;
  }

  /** Event horizon eats player shots during the void phase's shield. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    if (this.mode === 2 && this.shielded) {
      this.swallowed += bullets.collideCircle(
        true, this.x, this.y, this.def.radius + 24, () => {},
      );
      return { hits: 0, dealt: 0 };
    }
    return super.absorbBullets(bullets);
  }

  override takeDamage(amount: number): boolean {
    if (this.mode === 2 && this.shielded) return false;
    return super.takeDamage(amount);
  }

  override render(alpha: number): void {
    super.render(alpha);
    // Body shifts to the active element's color; flashes white on hit
    this.body.tint = this.hitFlash > 0 ? 0xffffff : MODE_TINT[this.mode] ?? 0xffffff;
    this.body.rotation += (this.mode === 2 && this.shielded ? 0.05 : 0.02);
    this.view.alpha = this.mode === 2 && this.shielded ? 0.85 : 1;
  }
}
